import React, { useEffect, useState } from 'react'
import './manage-landing.styles.scss';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import GameDashboard from './game-dashboard/game-dashboard.component';
import PlayerListContainer from './players/player-list-container.component';
import GameElements from './create-elements/create-elements-container.component';
import DistributePage from './distribute/distribute-page.component';
import GameSettings from './settings/game-settings.component';
import LeaderboardsLandingPage from './leaderboards/leaderboard-landing-page.component';
import TeamsLandingPage from './teams/teams-landing.component';
import { RandomString } from '@/utils/randomString';
import { format } from 'date-fns';
import ManagerActivityList from './activity-list/manager-activity-list.component';
import TransactionView from '../SHARED/transaction-view/transaction-view.component';
import ManageContributions from './contributions/manage-contributions.component';
import ManageLotteryLanding from './lottery/manage-lottery-landing.component';
import { useSelector } from 'react-redux';
import QrCodeScanner from './qr-code-scanner/qr-code-scanner.component';
import ActionItemsV2 from './action-items/action-items-v2.component';
import VideoModal from '../SHARED/video-modal/video-modal.component';
import { ModalAlert } from '../modal/modal.component';
import ManageMarketplaceLanding from './marketplace/manage-marketplace-landing.component';

const ManageLanding = ({
    gameData,
    gameElements,
    playerList,
    defaultActivityRecords,
    gameActivity,
    actionItems,
    changeActionItemStatus,
    gameStats,
    teamMembers,
    passcodes,
    latestMessage,
    gameManagers,
    activityCounts,
    orgs,
    role
}) => {

    const screenSize = useSelector(state => state.contentData.screenSize);
    const [purchases, setPurchases] = useState(null);
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const { gamePath } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const seenIntroVideo = localStorage.getItem(`seenIntroVideo`);
        if (!seenIntroVideo) {
            setShowIntroVideo(true);
        }
    }, []);

    useEffect(() => {
        if (!gamePath || !gameData) return;
        // console.log(gamePath, gameData.path);
        if (gamePath !== gameData.path) {
            navigate('/404')
        }
    }, [gamePath, gameData])

    useEffect(() => {
        if (!location) return;
        document.getElementById('scroll-to-top').scroll({ 'top': 0, 'behavior': 'smooth' });
    }, [location])

    useEffect(() => {
        if (!gameActivity || !playerList || !gameElements) return;
        let purchasesObj = {}
        for (let p of Object.values(gameActivity).filter(p => p.type === 'purchase')) {
            if (!p.data || !playerList[p.playerId]) continue;
            let items = Object.values(p.data);
            for (let i of items) {
                let itemData = gameElements[i.id];
                if (!itemData) continue;
                let item = {
                    ...i,
                    ...{
                        'playerId': p.playerId,
                        'ts': p.ts,
                        'purchaseId': p.id,
                        'purchaseInstanceId': p.ts + RandomString(8),
                        'date': format(new Date(p.ts), "Pp"),
                        'fName': playerList[p.playerId].fName,
                        'lName': playerList[p.playerId].lName,
                        'info': playerList[p.playerId].info,
                        'path': p.path,
                    }
                };
                if (!item.cost) {
                    item.cost = itemData.opt_marketPrice ?? 0
                }
                if (item.title) {
                    item.name = item.title;
                    delete item.title;
                }
                purchasesObj[item.purchaseInstanceId] = item
            }
        }
        setPurchases({ ...purchasesObj });
    }, [gameActivity, playerList, gameElements])

    function closeIntroVideo() {
        localStorage.setItem(`seenIntroVideo`, true);
        setShowIntroVideo(false);
        setAlertMessage(`For more tips for using Gamably, check out our Resource Center in the Management Menu!`);
    }

    return (
        <div className={`manage-landing-content ${screenSize}`} id='scroll-to-top'>
            <Routes>
                <Route
                    path=''
                    element={
                        <GameDashboard
                            gameData={gameData}
                            defaultActivityRecords={defaultActivityRecords}
                            playerList={playerList}
                            gameElements={gameElements}
                            latestMessage={latestMessage}
                            actionItems={actionItems}
                            gameManagers={gameManagers}
                            activityCounts={activityCounts}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='players/*'
                    element={
                        <PlayerListContainer
                            gameData={gameData}
                            gameElements={gameElements}
                            playerList={playerList}
                            gameStats={gameStats}
                            passcodes={passcodes}
                            teamMembers={teamMembers}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='teams/*'
                    element={
                        <TeamsLandingPage
                            gameData={gameData}
                            playerList={playerList}
                            gameElements={gameElements}
                            teamMembers={teamMembers}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='elements/*'
                    element={
                        <GameElements
                            gameData={gameData}
                            gameElements={gameElements}
                            playerList={playerList}
                            gameStats={gameStats}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='distribute'
                    element={
                        <DistributePage
                            gameData={gameData}
                            gameStats={gameStats}
                            gameElements={gameElements}
                            playerList={playerList}
                            teamMembers={teamMembers}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='settings/*'
                    element={
                        <GameSettings
                            gameData={gameData}
                            playerList={playerList}
                            orgs={orgs}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='actionitems/*'
                    element={
                        <ActionItemsV2
                            gameData={gameData}
                            gameStats={gameStats}
                            gameElements={gameElements}
                            playerList={playerList}
                            pendingActionItems={actionItems}
                            role={role}
                            changeActionItemStatus={changeActionItemStatus}
                        />
                    }
                ></Route>
                <Route
                    path='realworlddist/*'
                    element={
                        <ActionItemsV2
                            gameData={gameData}
                            gameElements={gameElements}
                            playerList={playerList}
                            pendingActionItems={actionItems}
                            changeActionItemStatus={changeActionItemStatus}
                        />
                    }
                ></Route>
                <Route
                    path='leaderboards/*'
                    element={
                        <LeaderboardsLandingPage
                            gameData={gameData}
                            playerList={playerList}
                            gameElements={gameElements}
                            role={role}
                        />
                    }
                ></Route>
                <Route
                    path='marketplace/*'
                    element={
                        <ManageMarketplaceLanding
                            gameData={gameData}
                            playerList={playerList}
                            teamMembers={teamMembers}
                            gameElements={gameElements}
                            defaultActivityRecords={defaultActivityRecords}
                            gameStats={gameStats}
                        />
                    }
                ></Route>
                <Route
                    path='activity/*'
                    element={
                        <ManagerActivityList
                            gameData={gameData}
                            defaultActivityRecords={defaultActivityRecords}
                            playerList={playerList}
                            gameElements={gameElements}
                            gameManagers={gameManagers} />
                    }
                ></Route>
                <Route
                    path='contributions'
                    element={
                        <ManageContributions
                            gameData={gameData}
                            playerList={playerList}
                            gameStats={gameStats}
                            gameElements={gameElements} />
                    }
                ></Route>
                <Route
                    path='lottery/*'
                    element={
                        <ManageLotteryLanding
                            gameData={gameData}
                            playerList={playerList}
                            gameStats={gameStats}
                            gameElements={gameElements} />
                    }
                ></Route>
                <Route
                    path='transaction/:transPath'
                    element={
                        <div className='transaction-container'>
                            <div className='transaction-card g-card'>
                                <TransactionView
                                    playerList={playerList}
                                    gameActivity={gameActivity}
                                    gameElements={gameElements}
                                    gameManagers={gameManagers} />
                            </div>
                        </div>
                    }
                ></Route>
                <Route path='scan' element={
                    <QrCodeScanner />
                }></Route>
                <Route path="*" element={<Navigate replace to="/404" />} />
            </Routes>
            <VideoModal
              show={showIntroVideo}
              cancel={() => closeIntroVideo()}
              platform='youtube'
              videoId='e--KYQug3kM'
              title="Welcome to Gamably! Here's a quick introduction." />
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
        </div>
    )
}

export default ManageLanding